import React, { forwardRef } from 'react'
import Icon, { IconSize } from '../Icon'
import styled, { CSSObject } from '../themes/styled'
import Text, { TextScale } from '../Text'
import {
    ButtonProps,
    withLinkWrapper,
    ButtonContainer as BaseButtonContainer,
    Loader,
    ButtonForm as BaseButtonForm,
} from '.'

export type ButtonSize = 'l' | 'm' | 's'
type ButtonForm = Exclude<BaseButtonForm, 'icon'>

interface ButtonContainerProps extends ButtonProps {
    size: ButtonSize
    form: ButtonForm
}

const ButtonContainer = styled(BaseButtonContainer)<ButtonContainerProps>`
    display: inline-flex;
    gap: ${({ theme }): string => theme.spacing[200]};
    ${({ size, form }) => sizeMap(form)[size].style}
`

export interface RectangleButtonProps extends ButtonProps {
    label?: string
    loadingText?: string
    size?: ButtonSize
    form?: ButtonForm
}

const sizeMap = (
    form?: ButtonForm,
): Record<ButtonSize, { iconSize: IconSize; textScale: TextScale; style: CSSObject }> => ({
    l: {
        iconSize: '24px',
        textScale: 'bodyM700',
        style: {
            minWidth: '120px',
            minHeight: '40px',
            padding: form === 'outlined' ? '6.5px 14.5px' : '8px 16px',
        },
    },
    m: {
        iconSize: '20px',
        textScale: 'bodyS700',
        style: {
            minWidth: '100px',
            minHeight: '32px',
            padding: form === 'outlined' ? '4.5px 14.5px' : '6px 16px',
        },
    },
    s: {
        iconSize: '16px',
        textScale: 'bodyS700',
        style: {
            minWidth: '84px',
            minHeight: '28px',
            padding: form === 'outlined' ? '2.5px 6.5px' : '4px 8px',
        },
    },
})

const RectangleButton = forwardRef<HTMLButtonElement, RectangleButtonProps>(
    (
        {
            size = 'm',
            color = 'primary',
            form = 'filled',
            icon,
            iconForm,
            iconFillColor,
            label,
            loadingText,
            isDisabled = false,
            isLoading = false,
            ...props
        },
        ref,
    ) => {
        const { iconSize, textScale } = sizeMap(form)[size]
        return (
            <ButtonContainer
                ref={ref}
                size={size}
                color={color}
                form={form}
                isDisabled={isDisabled}
                isLoading={isLoading}
                {...props}
            >
                {isLoading ? (
                    <Loader size={size} label={loadingText} spinnerColor="currentColor" />
                ) : (
                    <>
                        {icon && (
                            <Icon
                                className="button-icon"
                                name={icon}
                                form={iconForm}
                                size={iconSize}
                                color="currentColor"
                                fillColor={iconFillColor}
                            />
                        )}
                        <Text className="button-label" scale={textScale}>
                            {label}
                        </Text>
                    </>
                )}
            </ButtonContainer>
        )
    },
)

RectangleButton.displayName = 'RectangleButton'

export default RectangleButton
export const RectangleLinkButton = withLinkWrapper(RectangleButton)
